import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  BellRing, CheckCircle2, Loader2, ExternalLink, ShieldCheck, Settings2,
  AlertTriangle, RotateCcw, Save, Inbox,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { api } from '../../lib/api';
import { formatDate } from '../../components/Shared';

/* Admin -> Needs Attention (staff-only inbox).
   Lists everything residents have posted, requested, submitted, or updated
   that still needs Steve's review. Summaries are privacy-lean on purpose:
   full resident details stay inside each item's admin record page. */

export const notifyAttentionChanged = () => window.dispatchEvent(new Event('attention-changed'));

const STATUS_LABELS = {
  new: 'New', needs_review: 'Needs Review', in_progress: 'In Progress',
  reviewed: 'Reviewed', archived: 'Archived',
};
const STATUS_BADGE = {
  new: 'bg-[#EAF2FF] text-[#1a2744] border-blue-200',
  needs_review: 'bg-amber-50 text-[#B54708] border-amber-200',
  in_progress: 'bg-violet-50 text-[#6d28d9] border-violet-200',
  reviewed: 'bg-emerald-50 text-[#067647] border-emerald-200',
  archived: 'bg-slate-100 text-slate-500 border-slate-300',
};

const ItemRow = ({ item, onStatus }) => {
  const urgent = item.priority === 'urgent';
  const inactive = ['reviewed', 'archived'].includes(item.review_status);
  return (
    <div
      className={`border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-2.5 ${urgent && !inactive ? 'border-red-300 bg-red-50/60' : inactive ? 'border-dashed border-slate-200 bg-slate-50 opacity-70' : 'border-slate-200 bg-white'}`}
      data-testid={`attention-item-${item.key}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          {urgent && <Badge className="bg-red-600 text-white hover:bg-red-600 text-[10px] font-black" data-testid={`attention-urgent-${item.key}`}><AlertTriangle className="h-3 w-3 mr-0.5" /> URGENT</Badge>}
          <Badge variant="outline" className="text-[10px] font-bold text-slate-500">{item.type_label}</Badge>
          <Badge variant="outline" className={`text-[10px] font-bold ${STATUS_BADGE[item.review_status] || ''}`} data-testid={`attention-status-badge-${item.key}`}>
            {STATUS_LABELS[item.review_status] || item.review_status}
          </Badge>
        </div>
        <p className="text-sm font-bold text-[#1a2744] mt-1 truncate">{item.title}</p>
        <p className="text-[11px] text-slate-500 truncate">
          {item.received_at ? `Received ${formatDate(item.received_at)}` : 'Date unknown'} · {item.source}{item.extra ? ` · ${item.extra}` : ''}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 shrink-0">
        <Select value={item.review_status} onValueChange={(v) => onStatus(item.key, v)}>
          <SelectTrigger className="h-9 w-[140px] text-xs font-bold" data-testid={`attention-status-select-${item.key}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v} className="text-xs">{l}</SelectItem>)}
          </SelectContent>
        </Select>
        {!inactive && (
          <Button size="sm" variant="outline" className="h-9 text-xs font-bold border-slate-300" onClick={() => onStatus(item.key, 'reviewed')} data-testid={`attention-review-btn-${item.key}`}>
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark reviewed
          </Button>
        )}
        <Button asChild size="sm" className="h-9 text-xs font-bold bg-[#1a2744] hover:bg-[#243456] text-white" data-testid={`attention-open-btn-${item.key}`}>
          <Link to={item.link}><ExternalLink className="h-3.5 w-3.5 mr-1" /> Open</Link>
        </Button>
      </div>
    </div>
  );
};

const SettingsCard = ({ data, onSaved }) => {
  const [cfg, setCfg] = useState(data.settings);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));
  const setType = (k, v) => setCfg((p) => ({ ...p, enabled_types: { ...p.enabled_types, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/admin/attention/settings', cfg);
      toast.success('Notification settings saved.');
      onSaved(r.data.settings);
      notifyAttentionChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save settings'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="border-slate-200" data-testid="attention-settings-card">
      <CardContent className="p-4 space-y-4">
        <p className="font-bold text-[#1a2744] flex items-center gap-1.5 text-sm"><Settings2 className="h-4 w-4" /> Notification settings</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="flex items-center justify-between gap-2 border border-slate-200 rounded-lg p-2.5 text-xs font-bold text-slate-700">
            Show alert on the dashboard after login
            <Switch checked={cfg.show_dashboard_alert} onCheckedChange={(v) => set('show_dashboard_alert', v)} data-testid="attention-setting-dashboard-alert" />
          </label>
          <label className="flex items-center justify-between gap-2 border border-slate-200 rounded-lg p-2.5 text-xs font-bold text-slate-700">
            Show the sidebar count badge
            <Switch checked={cfg.show_login_alert} onCheckedChange={(v) => set('show_login_alert', v)} data-testid="attention-setting-login-alert" />
          </label>
          <label className="flex items-center justify-between gap-2 border border-slate-200 rounded-lg p-2.5 text-xs font-bold text-slate-700">
            Mark items reviewed automatically when opened
            <Switch checked={cfg.auto_review_on_open} onCheckedChange={(v) => set('auto_review_on_open', v)} data-testid="attention-setting-auto-review" />
          </label>
          <div className="border border-slate-200 rounded-lg p-2.5">
            <p className="text-xs font-bold text-slate-700 mb-1">Flag unresolved issues older than (days)</p>
            <Input type="number" min={1} max={365} value={cfg.stale_days} onChange={(e) => set('stale_days', e.target.value)} className="h-9 w-28 text-sm" data-testid="attention-setting-stale-days" />
          </div>
        </div>
        <div>
          <p className="text-xs font-bold text-slate-700 mb-1">Admin notification email</p>
          <Input value={cfg.notification_email} onChange={(e) => set('notification_email', e.target.value)} className="h-10 max-w-sm text-sm" data-testid="attention-setting-email" />
        </div>
        <div>
          <p className="text-xs font-bold text-slate-700 mb-1.5">Which item types count as needs attention</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {data.types.map((t) => (
              <label key={t.key} className="flex items-center justify-between gap-2 border border-slate-200 rounded-lg px-2.5 py-2 text-xs font-semibold text-slate-600">
                {t.label}
                <Switch checked={cfg.enabled_types[t.key] !== false} onCheckedChange={(v) => setType(t.key, v)} className="scale-90" data-testid={`attention-setting-type-${t.key}`} />
              </label>
            ))}
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="attention-settings-save-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save settings
        </Button>
      </CardContent>
    </Card>
  );
};

export default function AdminNeedsAttention() {
  const [data, setData] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsData, setSettingsData] = useState(null);

  const load = useCallback((inactive = showInactive) => {
    api.get('/admin/attention/items', { params: { include_inactive: inactive } })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Could not load the attention inbox'));
  }, [showInactive]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/admin/attention/settings').then((r) => setSettingsData(r.data)).catch(() => {});
  }, []);

  const setStatus = async (key, status) => {
    try {
      await api.put('/admin/attention/item/status', { key, status });
      toast.success(`Marked as ${STATUS_LABELS[status] || status}.`);
      load();
      notifyAttentionChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update the item'); }
  };

  const reviewAll = async (typeKey, label) => {
    if (!window.confirm(`Mark every "${label}" item as reviewed?\n\nThe original records are kept - this only clears them from the attention count.`)) return;
    try {
      const r = await api.post(`/admin/attention/group/${typeKey}/review-all`);
      toast.success(`${r.data.marked_reviewed} item(s) marked reviewed.`);
      load();
      notifyAttentionChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not mark the group reviewed'); }
  };

  if (!data) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const totalActive = data.total_active || 0;

  return (
    <div className="space-y-4" data-testid="admin-needs-attention">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl font-black text-[#1a2744] flex items-center gap-2">
            <BellRing className="h-6 w-6" /> Needs Attention
            <Badge className={`${totalActive ? 'bg-[#C9A84C] text-[#1a2744]' : 'bg-emerald-100 text-[#067647]'} hover:bg-inherit font-black`} data-testid="attention-total-badge">{totalActive}</Badge>
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Everything residents have posted, requested, or submitted that still needs review, in one place.
            Marking an item reviewed clears it from the count without deleting anything.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => { setShowInactive((v) => { load(!v); return !v; }); }} className="h-10 text-xs font-bold border-slate-300" data-testid="attention-toggle-inactive-btn">
            {showInactive ? 'Hide reviewed & archived' : 'Show reviewed & archived'}
          </Button>
          <Button variant="outline" onClick={() => setShowSettings((v) => !v)} className="h-10 text-xs font-bold border-slate-300" data-testid="attention-toggle-settings-btn">
            <Settings2 className="h-3.5 w-3.5 mr-1.5" /> Settings
          </Button>
        </div>
      </div>

      {showSettings && settingsData && (
        <SettingsCard data={settingsData} onSaved={(s) => { setSettingsData((d) => ({ ...d, settings: s })); load(); }} />
      )}

      {totalActive === 0 && !showInactive && (
        <Card className="border-emerald-200 bg-emerald-50" data-testid="attention-all-clear">
          <CardContent className="p-6 flex items-center gap-3">
            <CheckCircle2 className="h-8 w-8 text-[#067647] shrink-0" />
            <div>
              <p className="font-bold text-[#065f46]">No new items need attention right now.</p>
              <p className="text-sm text-emerald-800 mt-0.5">New reports, requests, and submissions will appear here automatically.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {data.groups.map((g) => {
        if (!g.items.length) return null;
        return (
          <Card key={g.key} className="border-slate-200" data-testid={`attention-group-${g.key}`}>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <p className="font-serif font-black text-[#1a2744] flex items-center gap-2">
                  <Inbox className="h-4 w-4" /> {g.label}
                  <Badge variant="outline" className="font-black text-xs" data-testid={`attention-group-count-${g.key}`}>{g.active_count}</Badge>
                </p>
                {g.active_count > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {[...new Set(g.items.filter((i) => !['reviewed', 'archived'].includes(i.review_status)).map((i) => i.type))].map((tk) => (
                      <Button key={tk} size="sm" variant="ghost" className="h-8 text-[11px] font-bold text-slate-500 hover:text-[#1a2744]"
                        onClick={() => reviewAll(tk, g.items.find((i) => i.type === tk)?.type_label || tk)}
                        data-testid={`attention-review-all-${tk}`}>
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Review all: {g.items.find((i) => i.type === tk)?.type_label}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                {g.items.map((it) => <ItemRow key={it.key} item={it} onStatus={setStatus} />)}
              </div>
            </CardContent>
          </Card>
        );
      })}

      <Card className="border-slate-200 bg-slate-50"><CardContent className="p-4 text-xs text-slate-600 space-y-1">
        <p className="font-bold text-[#1a2744] flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Privacy</p>
        <p>{data.privacy_note}</p>
        <p>This inbox is admin-only and never appears publicly. Nothing is ever deleted when you mark items reviewed or archived.</p>
      </CardContent></Card>
    </div>
  );
}
